import React, { useState } from 'react';
import {
  TextField,
  Button,
  Container,
  Typography,
  Alert,
  Grid,
  Box,
  IconButton,
  InputAdornment,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  School,
  Security,
  Speed,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import cbt from '../assets/cbt.png';
import api from '../api';

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  console.log('▶ NODE_ENV:', process.env.NODE_ENV);
  console.log('▶ BASE_URI:', process.env.REACT_APP_BASE_URI);

  const handleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      const res = await api.post('/api/login', { username, password });
      const { token, role, needsPasswordChange } = res.data;

      localStorage.setItem('token', token);
      localStorage.setItem('role', role);

      if (needsPasswordChange) {
        navigate('/change-password');
      } else if (role === 'teacher') {
        navigate('/questionbank');
      } else if (role === 'qa') {
        navigate('/qa');
      } else if (role === 'admin') {
        navigate('/dashboard');
      } else if (role === 'superadmin') {
        navigate('/dashboard');
      } else {
        setError('Unknown user role.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <School sx={{ fontSize: 24 }} />, title: 'Smart Testing' },
    { icon: <Security sx={{ fontSize: 24 }} />, title: 'Secure Platform' },
    { icon: <Speed sx={{ fontSize: 24 }} />, title: 'Fast & Reliable' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f5f7fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in={true} timeout={800}>
          <Grid container spacing={0} sx={{
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: 'white',
            minHeight: 600,
          }}>
            {/* Left Panel - Branding */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: { xs: 4, md: 6 },
                borderRight: '1px solid #e8eaf0',
              }}
            >
              <Zoom in={true} timeout={1000}>
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  {/* Logo */}
                  <Box
                    sx={{
                      mb: 4,
                      display: 'inline-block',
                    }}
                  >
                    <img
                      src={cbt}
                      alt="CBT Logo"
                      style={{
                        width: '220px',
                        height: '220px',
                        display: 'block',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08))',
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: '#1a202c',
                      mb: 2,
                      fontSize: { xs: '1.75rem', md: '2.5rem' },
                      fontFamily: '"Poppins", "Inter", sans-serif',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    CBT PORTAL
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#4a5568',
                      fontWeight: 500,
                      mb: 1,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Computer Based Testing Service
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#718096',
                      mb: 5,
                      fontSize: { xs: '0.875rem', md: '0.95rem' },
                      maxWidth: '400px',
                      mx: 'auto',
                      fontFamily: '"Inter", sans-serif',
                      lineHeight: 1.6,
                    }}
                  >
                    Aror University of Art, Architecture, Design & Heritage Sukkur
                  </Typography>

                  {/* Features */}
                  <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
                    {features.map((feature, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Zoom in={true} timeout={1200 + index * 200}>
                          <Box
                            sx={{
                              p: 2.5,
                              textAlign: 'center',
                              borderRadius: 2,
                              background: '#f7fafc',
                              border: '1px solid #e2e8f0',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: '#edf2f7',
                                transform: 'translateY(-3px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                p: 1.5,
                                borderRadius: '50%',
                                background: '#ffffff',
                                color: '#2d3748',
                                mb: 1.5,
                                border: '1px solid #e2e8f0',
                              }}
                            >
                              {feature.icon}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ 
                                fontWeight: 600, 
                                color: '#2d3748', 
                                fontSize: '0.875rem',
                                fontFamily: '"Inter", sans-serif',
                              }}
                            >
                              {feature.title}
                            </Typography>
                          </Box>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Zoom>
            </Grid>

            {/* Right Panel - Login Form */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: { xs: 4, md: 6 },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  right: '-30%',
                  width: '400px',
                  height: '400px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.08)',
                  filter: 'blur(60px)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-30%',
                  left: '-20%',
                  width: '350px',
                  height: '350px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.06)',
                  filter: 'blur(50px)',
                },
              }}
            >
              <Fade in={true} timeout={1200}>
                <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
                  <Box sx={{ mb: 4 }}>
                    {/* Welcome Header */}
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: 'white',
                          mb: 1,
                          fontFamily: '"Poppins", "Inter", sans-serif',
                          letterSpacing: '-0.5px',
                        }}
                      >
                        Welcome Back
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.85)',
                          fontFamily: '"Inter", sans-serif',
                        }}
                      >
                        Sign in to continue to your account
                      </Typography>
                    </Box>

                    {error && (
                      <Fade in={true}>
                        <Alert
                          severity="error"
                          sx={{
                            mb: 3,
                            borderRadius: 2,
                            animation: 'shake 0.5s',
                            fontFamily: '"Inter", sans-serif',
                            '@keyframes shake': {
                              '0%, 100%': { transform: 'translateX(0)' },
                              '25%': { transform: 'translateX(-10px)' },
                              '75%': { transform: 'translateX(10px)' },
                            },
                          }}
                          onClose={() => setError('')}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    {/* Username Field */}
                    <TextField
                      label="Username"
                      fullWidth
                      margin="normal"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onFocus={() => setFocusedField('username')}
                      onBlur={() => setFocusedField('')}
                      disabled={loading}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: 'white',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '1rem',
                          '&:hover': {
                            backgroundColor: 'white',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.15)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.25)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                              borderWidth: 2.5,
                            },
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: '"Inter", sans-serif',
                          color: '#4a5568',
                          fontWeight: 500,
                          fontSize: '1rem',
                          '&.Mui-focused': {
                            color: '#2d3748',
                            fontWeight: 600,
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#1a202c',
                          fontWeight: 500,
                          '&::placeholder': {
                            color: '#718096',
                            opacity: 1,
                          },
                        },
                      }}
                    />

                    {/* Password Field */}
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField('')}
                      onKeyPress={(e) => e.key === 'Enter' && !loading && handleLogin()}
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{
                                color: '#718096',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  color: '#667eea',
                                  transform: 'scale(1.15)',
                                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                },
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: 'white',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '1rem',
                          '&:hover': {
                            backgroundColor: 'white',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.15)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.25)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                              borderWidth: 2.5,
                            },
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: '"Inter", sans-serif',
                          color: '#4a5568',
                          fontWeight: 500,
                          fontSize: '1rem',
                          '&.Mui-focused': {
                            color: '#2d3748',
                            fontWeight: 600,
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#1a202c',
                          fontWeight: 500,
                          '&::placeholder': {
                            color: '#718096',
                            opacity: 1,
                          },
                        },
                      }}
                    />

                    {/* Sign In Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleLogin}
                      disabled={loading || !username || !password}
                      startIcon={loading ? null : <LoginIcon />}
                      sx={{
                        py: 1.8,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        fontFamily: '"Poppins", "Inter", sans-serif',
                        letterSpacing: '0.3px',
                        background: 'white',
                        color: '#667eea',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                        textTransform: 'none',
                        '&:hover': {
                          background: '#f7fafc',
                          color: '#5568d3',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&.Mui-disabled': {
                          background: 'rgba(255, 255, 255, 0.5)',
                          color: 'rgba(102, 126, 234, 0.5)',
                          boxShadow: 'none',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </Box>

                  {/* Footer */}
                  <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid rgba(255, 255, 255, 0.2)', textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontWeight: 500,
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      Developed By: IT Team Aror University
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        display: 'block',
                        mt: 0.5,
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      2025 | Version: 2.0.2
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Fade>
      </Container>
    </Box>
  );
}

export default Login;
